<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CSAT_DISPLAY_TYPES } from 'shared/constants/messages';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ActionCard from 'dashboard/components-next/message/bubbles/ActionCard.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
});

const { t } = useI18n();
const store = useStore();

const blankChoices = () => [{ title: '' }, { title: '' }];
const blankFields = () => [
  { label: '', name: 'campo_1', type: 'text', required: true },
];
const blankPayment = () => ({ amount: '', description: '', link: '' });
const blankDrafts = () => ({
  buttons: '',
  list: '',
  csat: '',
  form: '',
  payment: '',
});

const open = ref(false);
const sending = ref(false);
const kind = ref('buttons');
const drafts = ref(blankDrafts());
const choices = ref(blankChoices());
const formFields = ref(blankFields());
const payment = ref(blankPayment());

const prompt = computed({
  get: () => drafts.value[kind.value] || '',
  set: value => {
    drafts.value = { ...drafts.value, [kind.value]: value };
  },
});

const resetDraft = () => {
  kind.value = 'buttons';
  drafts.value = blankDrafts();
  choices.value = blankChoices();
  formFields.value = blankFields();
  payment.value = blankPayment();
};

const ACTIONS = [
  { key: 'buttons', icon: 'i-ph-squares-four' },
  { key: 'list', icon: 'i-ph-list-bullets' },
  { key: 'csat', icon: 'i-ph-star' },
  { key: 'form', icon: 'i-ph-textbox' },
  { key: 'payment', icon: 'i-ph-currency-circle-dollar' },
];

const filledChoices = computed(() =>
  choices.value.map(item => item.title.trim()).filter(Boolean)
);

const filledFields = computed(() =>
  formFields.value.filter(field => field.label.trim())
);

const previewText = computed(() => {
  if (prompt.value.trim()) return prompt.value.trim();
  if (kind.value === 'csat') {
    return t('CONVERSATION.REPLYBOX.USER_ACTIONS.CSAT_DEFAULT');
  }
  if (kind.value === 'payment') {
    return t('CONVERSATION.REPLYBOX.USER_ACTIONS.PAYMENT_DEFAULT');
  }
  return t('CONVERSATION.REPLYBOX.USER_ACTIONS.PREVIEW_EMPTY');
});

const previewKind = computed(() => {
  if (kind.value === 'csat') return 'rate';
  if (kind.value === 'payment') return 'pay';
  if (kind.value === 'form') return 'form';
  return 'choice';
});

const previewOptions = computed(() => {
  if (kind.value === 'buttons' || kind.value === 'list') {
    return filledChoices.value.length
      ? filledChoices.value
      : [t('CONVERSATION.REPLYBOX.USER_ACTIONS.OPTION_N', { n: 1 })];
  }
  if (kind.value === 'form') {
    return filledFields.value.map(field => field.label);
  }
  return [];
});

const previewPayHref = computed(() => {
  const raw = payment.value.link.trim();
  if (!raw) return '';
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
});

const canSend = computed(() => {
  if (kind.value === 'buttons' || kind.value === 'list') {
    return filledChoices.value.length >= 1;
  }
  if (kind.value === 'form') return filledFields.value.length >= 1;
  if (kind.value === 'payment') return Boolean(payment.value.link.trim());
  return true;
});

const sendHint = computed(() => {
  if (canSend.value) return '';
  if (kind.value === 'buttons' || kind.value === 'list') {
    return t('CONVERSATION.REPLYBOX.USER_ACTIONS.NEED_OPTION');
  }
  if (kind.value === 'form') {
    return t('CONVERSATION.REPLYBOX.USER_ACTIONS.NEED_FIELD');
  }
  if (kind.value === 'payment') {
    return t('CONVERSATION.REPLYBOX.USER_ACTIONS.NEED_LINK');
  }
  return '';
});

const openSheet = () => {
  open.value = true;
};

const closeSheet = () => {
  if (sending.value) return;
  open.value = false;
  resetDraft();
};

const onKeydown = event => {
  if (event.key === 'Escape') closeSheet();
};

const lockPage = locked => {
  document.body.style.overflow = locked ? 'hidden' : '';
  if (locked) {
    document.addEventListener('keydown', onKeydown);
  } else {
    document.removeEventListener('keydown', onKeydown);
  }
};

watch(open, locked => lockPage(locked));
onBeforeUnmount(() => lockPage(false));

const addChoice = () => {
  if (choices.value.length >= 10) return;
  choices.value.push({ title: '' });
};

const removeChoice = index => {
  if (choices.value.length <= 1) return;
  choices.value.splice(index, 1);
};

const addField = () => {
  formFields.value.push({
    label: '',
    name: `campo_${formFields.value.length + 1}`,
    type: 'text',
    required: false,
  });
};

const removeField = index => {
  if (formFields.value.length <= 1) return;
  formFields.value.splice(index, 1);
};

const slugify = value =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w]+/g, '_')
    .replace(/^_|_$/g, '') || 'opcao';

const buildPayload = () => {
  if (kind.value === 'buttons' || kind.value === 'list') {
    return {
      message: previewText.value,
      content_type: 'input_select',
      contentAttributes: {
        display: kind.value,
        items: filledChoices.value.map(title => ({
          title,
          value: slugify(title),
        })),
      },
    };
  }
  if (kind.value === 'csat') {
    return {
      message: previewText.value,
      content_type: 'input_csat',
      contentAttributes: {
        displayType: CSAT_DISPLAY_TYPES.STAR,
      },
    };
  }
  if (kind.value === 'form') {
    return {
      message: previewText.value,
      content_type: 'form',
      contentAttributes: {
        items: filledFields.value.map((field, index) => ({
          type: field.type || 'text',
          label: field.label.trim(),
          name: slugify(field.label) || `campo_${index + 1}`,
          placeholder: field.label.trim(),
          required: Boolean(field.required),
        })),
      },
    };
  }
  return {
    message: previewText.value,
    content_type: 'text',
    contentAttributes: {
      payment: {
        amount: payment.value.amount.trim(),
        link: payment.value.link.trim(),
        description: payment.value.description.trim(),
      },
    },
  };
};

const sendAction = async () => {
  if (!canSend.value || sending.value) return;
  sending.value = true;
  try {
    await store.dispatch('createPendingMessageAndSend', {
      conversationId: props.conversationId,
      private: false,
      ...buildPayload(),
    });
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
    emitter.emit(BUS_EVENTS.MESSAGE_SENT);
    resetDraft();
    open.value = false;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CONVERSATION.MESSAGE_ERROR'));
  } finally {
    sending.value = false;
  }
};
</script>

<template>
  <div class="relative">
    <NextButton
      v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.USER_ACTIONS.TIP')"
      icon="i-ph-lightning"
      slate
      faded
      sm
      data-testid="composer-user-actions"
      :aria-expanded="open"
      :aria-label="$t('CONVERSATION.REPLYBOX.USER_ACTIONS.TIP')"
      @click="openSheet"
    />

    <Teleport to="body">
      <Transition
        enter-active-class="duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        leave-active-class="duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          v-if="open"
          class="fixed inset-0 z-[80] flex items-end justify-center p-3 md:items-center md:p-6"
        >
          <button
            type="button"
            class="absolute inset-0 bg-black/70 backdrop-blur-[8px]"
            :aria-label="$t('CONVERSATION.REPLYBOX.USER_ACTIONS.CLOSE')"
            @click="closeSheet"
          />
          <section
            role="dialog"
            aria-modal="true"
            class="relative flex h-[min(92vh,46rem)] w-full max-w-4xl origin-bottom flex-col overflow-hidden rounded-[28px] border border-[#2F3336] bg-[#16181C] shadow-none md:origin-center"
          >
            <header
              class="flex shrink-0 items-center justify-between gap-3 border-b border-[#2F3336] px-5 py-4"
            >
              <div>
                <p
                  class="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-[#7856FF]"
                >
                  {{ $t('CONVERSATION.REPLYBOX.USER_ACTIONS.KICKER') }}
                </p>
                <h2 class="m-0 text-[20px] font-extrabold text-[#E7E9EA]">
                  {{ $t('CONVERSATION.REPLYBOX.USER_ACTIONS.TITLE') }}
                </h2>
              </div>
              <button
                type="button"
                class="flex size-9 items-center justify-center rounded-full text-[#71767B] hover:bg-white/10"
                :aria-label="$t('CONVERSATION.REPLYBOX.USER_ACTIONS.CLOSE')"
                @click="closeSheet"
              >
                <span class="i-ph-x size-4" />
              </button>
            </header>

            <div class="flex min-h-0 flex-1 flex-col md:flex-row">
              <nav
                class="flex shrink-0 gap-1 overflow-x-auto border-b border-[#2F3336] p-3 md:w-56 md:flex-col md:overflow-y-auto md:border-b-0 md:border-r"
              >
                <button
                  v-for="action in ACTIONS"
                  :key="action.key"
                  type="button"
                  class="flex min-h-11 min-w-[8.5rem] items-center gap-2.5 rounded-full px-3 py-2.5 text-left transition-colors duration-150 md:min-w-0"
                  :class="
                    kind === action.key
                      ? 'bg-[#7856FF]/15 text-[#7856FF]'
                      : 'text-[#E7E9EA] hover:bg-white/10'
                  "
                  @click="kind = action.key"
                >
                  <span class="size-4 shrink-0" :class="[action.icon]" />
                  <span class="min-w-0">
                    <span class="block truncate text-sm font-bold">
                      {{
                        $t(
                          `CONVERSATION.REPLYBOX.USER_ACTIONS.${action.key.toUpperCase()}`
                        )
                      }}
                    </span>
                    <span
                      class="hidden text-[11px] leading-snug text-[#71767B] md:block"
                    >
                      {{
                        $t(
                          `CONVERSATION.REPLYBOX.USER_ACTIONS.${action.key.toUpperCase()}_HINT`
                        )
                      }}
                    </span>
                  </span>
                </button>
              </nav>

              <div class="min-h-0 flex-1 overflow-y-auto p-5">
                <div class="grid gap-5 lg:grid-cols-[1fr_16rem]">
                  <div class="flex min-w-0 flex-col gap-4">
                    <label class="flex flex-col gap-1.5">
                      <span class="text-xs font-bold text-[#71767B]">
                        {{ $t('CONVERSATION.REPLYBOX.USER_ACTIONS.PROMPT') }}
                      </span>
                      <textarea
                        v-model="prompt"
                        rows="3"
                        class="reset-base !mb-0 min-h-[4.5rem] w-full resize-none rounded-2xl border-0 bg-[#202327] px-3 py-2.5 text-sm text-[#E7E9EA] outline-none placeholder:text-[#71767B] focus:ring-1 focus:ring-[#1D9BF0]"
                        :placeholder="
                          $t(
                            'CONVERSATION.REPLYBOX.USER_ACTIONS.PROMPT_PLACEHOLDER'
                          )
                        "
                      />
                    </label>

                    <div
                      v-if="kind === 'buttons' || kind === 'list'"
                      class="flex flex-col gap-2"
                    >
                      <div class="flex items-center justify-between">
                        <span class="text-xs font-bold text-[#71767B]">
                          {{ $t('CONVERSATION.REPLYBOX.USER_ACTIONS.OPTIONS') }}
                        </span>
                        <button
                          type="button"
                          class="text-xs font-bold text-[#7856FF] hover:underline"
                          @click="addChoice"
                        >
                          {{
                            $t('CONVERSATION.REPLYBOX.USER_ACTIONS.ADD_OPTION')
                          }}
                        </button>
                      </div>
                      <div
                        v-for="(choice, index) in choices"
                        :key="index"
                        class="flex items-center gap-2"
                      >
                        <input
                          v-model="choice.title"
                          type="text"
                          class="reset-base !mb-0 h-10 min-w-0 flex-1 rounded-full border-0 bg-[#202327] px-3 text-sm text-[#E7E9EA] outline-none placeholder:text-[#71767B] focus:ring-1 focus:ring-[#1D9BF0]"
                          :placeholder="
                            $t('CONVERSATION.REPLYBOX.USER_ACTIONS.OPTION_N', {
                              n: index + 1,
                            })
                          "
                        />
                        <button
                          type="button"
                          class="flex size-11 shrink-0 items-center justify-center rounded-full text-[#71767B] hover:bg-white/10"
                          :aria-label="
                            $t('CONVERSATION.REPLYBOX.USER_ACTIONS.REMOVE')
                          "
                          @click="removeChoice(index)"
                        >
                          <span class="i-ph-minus-circle size-4" />
                        </button>
                      </div>
                    </div>

                    <div
                      v-else-if="kind === 'form'"
                      class="flex flex-col gap-2"
                    >
                      <div class="flex items-center justify-between">
                        <span class="text-xs font-bold text-[#71767B]">
                          {{ $t('CONVERSATION.REPLYBOX.USER_ACTIONS.FIELDS') }}
                        </span>
                        <button
                          type="button"
                          class="text-xs font-bold text-[#7856FF] hover:underline"
                          @click="addField"
                        >
                          {{
                            $t('CONVERSATION.REPLYBOX.USER_ACTIONS.ADD_FIELD')
                          }}
                        </button>
                      </div>
                      <div
                        v-for="(field, index) in formFields"
                        :key="index"
                        class="flex items-center gap-2"
                      >
                        <input
                          v-model="field.label"
                          type="text"
                          class="reset-base !mb-0 h-10 min-w-0 flex-1 rounded-full border-0 bg-[#202327] px-3 text-sm text-[#E7E9EA] outline-none placeholder:text-[#71767B] focus:ring-1 focus:ring-[#1D9BF0]"
                          :placeholder="
                            $t('CONVERSATION.REPLYBOX.USER_ACTIONS.FIELD_N', {
                              n: index + 1,
                            })
                          "
                        />
                        <button
                          type="button"
                          class="flex size-11 shrink-0 items-center justify-center rounded-full text-[#71767B] hover:bg-white/10"
                          :aria-label="
                            $t('CONVERSATION.REPLYBOX.USER_ACTIONS.REMOVE')
                          "
                          @click="removeField(index)"
                        >
                          <span class="i-ph-minus-circle size-4" />
                        </button>
                      </div>
                    </div>

                    <div
                      v-else-if="kind === 'payment'"
                      class="flex flex-col gap-3"
                    >
                      <label class="flex flex-col gap-1.5">
                        <span class="text-xs font-bold text-[#71767B]">
                          {{ $t('CONVERSATION.REPLYBOX.USER_ACTIONS.AMOUNT') }}
                        </span>
                        <input
                          v-model="payment.amount"
                          type="text"
                          class="reset-base !mb-0 h-10 w-full rounded-full border-0 bg-[#202327] px-3 text-sm tabular-nums text-[#E7E9EA] outline-none focus:ring-1 focus:ring-[#1D9BF0]"
                          :placeholder="
                            $t(
                              'CONVERSATION.REPLYBOX.USER_ACTIONS.AMOUNT_PLACEHOLDER'
                            )
                          "
                        />
                      </label>
                      <label class="flex flex-col gap-1.5">
                        <span class="text-xs font-bold text-[#71767B]">
                          {{
                            $t('CONVERSATION.REPLYBOX.USER_ACTIONS.PAY_LINK')
                          }}
                        </span>
                        <input
                          v-model="payment.link"
                          type="url"
                          class="reset-base !mb-0 h-10 w-full rounded-full border-0 bg-[#202327] px-3 text-sm text-[#E7E9EA] outline-none focus:ring-1 focus:ring-[#1D9BF0]"
                          :placeholder="
                            $t(
                              'CONVERSATION.REPLYBOX.USER_ACTIONS.LINK_PLACEHOLDER'
                            )
                          "
                        />
                      </label>
                      <label class="flex flex-col gap-1.5">
                        <span class="text-xs font-bold text-[#71767B]">
                          {{
                            $t(
                              'CONVERSATION.REPLYBOX.USER_ACTIONS.PAY_DESCRIPTION'
                            )
                          }}
                        </span>
                        <input
                          v-model="payment.description"
                          type="text"
                          class="reset-base !mb-0 h-10 w-full rounded-full border-0 bg-[#202327] px-3 text-sm text-[#E7E9EA] outline-none focus:ring-1 focus:ring-[#1D9BF0]"
                        />
                      </label>
                    </div>

                    <p
                      v-else
                      class="m-0 rounded-2xl bg-[#202327] px-3 py-3 text-sm text-[#71767B]"
                    >
                      {{ $t('CONVERSATION.REPLYBOX.USER_ACTIONS.CSAT_HELP') }}
                    </p>
                  </div>

                  <aside>
                    <p class="mb-2 text-xs font-bold text-n-slate-11">
                      {{ $t('CONVERSATION.REPLYBOX.USER_ACTIONS.PREVIEW') }}
                    </p>
                    <div
                      class="w-72 max-w-full rounded-[20px] rounded-br-[5px] !bg-n-slate-3 px-3.5 py-3"
                    >
                      <ActionCard
                        :kind="previewKind"
                        :title="previewText"
                        :subtitle="
                          kind === 'payment' ? payment.description.trim() : ''
                        "
                        :options="previewOptions"
                        :pay-amount="payment.amount.trim()"
                        :pay-href="previewPayHref"
                      />
                    </div>
                  </aside>
                </div>
              </div>
            </div>

            <footer
              class="flex shrink-0 items-center justify-end gap-3 border-t border-[#2F3336] px-5 py-3"
            >
              <p v-if="sendHint" class="mr-auto text-xs text-[#71767B]">
                {{ sendHint }}
              </p>
              <NextButton
                slate
                faded
                sm
                :label="$t('CONVERSATION.REPLYBOX.USER_ACTIONS.CLOSE')"
                @click="closeSheet"
              />
              <NextButton
                sm
                :label="$t('CONVERSATION.REPLYBOX.USER_ACTIONS.SEND')"
                :disabled="!canSend || sending"
                :is-loading="sending"
                @click="sendAction"
              />
            </footer>
          </section>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
